/**
 * i18n — G6, "multi-language and multi-locale from day one".
 *
 *   "No user-visible string exists in source code. Every string is a key in a
 *    catalogue. Adding a language is a translated catalogue plus a configuration row —
 *    not a release."
 *
 * ICU MessageFormat via `src/i18n/icu-format.ts` over `intl-messageformat`, so plural
 * categories (Italian and Spanish differ from English), gender, ordinals and nested selects
 * are handled by the format and never by string concatenation or `if (count === 1)`. See
 * that file for why the formatter is ours rather than `i18next-icu`.
 *
 * FALLBACK CHAIN, exactly as G6 specifies: `locale → language → en-GB`. A missing key
 * renders the key LOUDLY in development and staging, and fails CI
 * (`npm run i18n:coverage`), so it never reaches production.
 *
 * CATALOGUE DISCOVERY: namespaces are collected from `src/features/registry.ts` — one
 * per discovered feature, plus `core`. A chapter agent adds
 * `locale/<locale>/<slug>.json` and it is loaded. There is no index to edit.
 */

import i18next, { type i18n as I18nInstance } from 'i18next';
import { IcuFormat } from './icu-format';
import { initReactI18next } from 'react-i18next';
import { SOURCE_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from '../lib/format';

export { SOURCE_LOCALE, SUPPORTED_LOCALES, type SupportedLocale };

/**
 * Every catalogue file, resolved by Vite at build time. A new locale directory or a new
 * feature namespace is picked up with no code change — G6 test f: "adding a fourth
 * locale in staging touches only catalogue files and one configuration row".
 */
const catalogues = import.meta.glob<Record<string, string>>('../../locale/*/*.json', {
  eager: true,
  import: 'default',
});

interface Resources {
  [locale: string]: { [namespace: string]: Record<string, string> };
}

export function collectResources(): Resources {
  const resources: Resources = {};
  for (const [filePath, catalogue] of Object.entries(catalogues)) {
    const match = /\/locale\/([^/]+)\/([^/]+)\.json$/.exec(filePath);
    if (!match) continue;
    const [, locale, namespace] = match;
    if (locale === undefined || namespace === undefined) continue;
    // The pseudo-locale is generated into locale/pseudo/ and is loaded only when the
    // build asks for it, so it can never be selected by accident in production.
    resources[locale] ??= {};
    resources[locale][namespace] = catalogue;
  }
  return resources;
}

export interface I18nOptions {
  readonly locale?: string;
  /** Enables the pseudo-locale for the G6 test d screenshot run. */
  readonly pseudo?: boolean;
  readonly debug?: boolean;
}

export async function initI18n(options: I18nOptions = {}): Promise<I18nInstance> {
  const resources = collectResources();
  const requested = options.pseudo === true ? 'pseudo' : options.locale ?? detectLocale();

  await i18next
    .use(IcuFormat as never)
    .use(initReactI18next)
    .init({
      // A message whose ICU syntax does not parse is a hard failure outside production:
      // silently falling back to the ICU source is how a broken plural ships.
      i18nFormat: { throwOnParseError: options.debug === true },
      resources,
      lng: requested,
      // G6: locale → language → en-GB.
      fallbackLng: {
        'es-ES': ['es-419', 'es', SOURCE_LOCALE],
        'es-MX': ['es-419', 'es', SOURCE_LOCALE],
        it: ['it-IT', SOURCE_LOCALE],
        es: ['es-419', SOURCE_LOCALE],
        en: [SOURCE_LOCALE],
        default: [SOURCE_LOCALE],
      },
      defaultNS: 'core',
      ns: Object.keys(resources[SOURCE_LOCALE] ?? { core: {} }),
      // Features call `useTranslation()` with no namespace, so an unqualified key must be
      // searched across every feature catalogue and not only in `core`. Without this a
      // feature key resolves nowhere and renders `⟦key⟧` — which is what the conformance
      // component tests caught, and what every screen had been doing unnoticed.
      fallbackNS: Object.keys(resources[SOURCE_LOCALE] ?? {}).filter((ns) => ns !== 'core'),
      // A missing key renders the key itself, loudly. Returning the key rather than an
      // empty string is what makes a gap visible in a screenshot instead of looking
      // like a deliberate blank.
      parseMissingKeyHandler: (key) => `⟦${key}⟧`,
      saveMissing: false,
      // Keys are flat and contain dots, which are meaningful — `grid.column.entity` is
      // one key, not a three-level path.
      keySeparator: false,
      nsSeparator: ':',
      interpolation: { escapeValue: false },
      returnNull: false,
      debug: options.debug === true,
    });

  document.documentElement.lang = i18next.language;
  // Direction is set from the resolved locale rather than hard-coded. G6: "Direction-
  // agnostic layout now, RTL later … no layout decision may make them expensive."
  document.documentElement.dir = 'ltr';

  return i18next;
}

export function detectLocale(): SupportedLocale {
  if (typeof navigator === 'undefined') return SOURCE_LOCALE;
  for (const candidate of navigator.languages ?? [navigator.language]) {
    const exact = SUPPORTED_LOCALES.find((l) => l.toLowerCase() === candidate.toLowerCase());
    if (exact) return exact;
    const language = candidate.split('-')[0]?.toLowerCase();
    const byLanguage = SUPPORTED_LOCALES.find((l) => l.split('-')[0]?.toLowerCase() === language);
    if (byLanguage) return byLanguage;
  }
  return SOURCE_LOCALE;
}

/**
 * G6 coverage gate: "a locale is not selectable in production until 100% of keys are
 * translated and reviewed. Partial locales are a worse experience than a consistent
 * second language."
 *
 * `npm run i18n:coverage` is the build-time gate; this is the runtime guard that keeps
 * a half-translated locale out of the picker even if the gate is somehow bypassed.
 */
export function selectableLocales(resources = collectResources()): readonly SupportedLocale[] {
  const source = resources[SOURCE_LOCALE];
  if (!source) return [SOURCE_LOCALE];
  const requiredKeys = Object.entries(source).flatMap(([ns, cat]) => Object.keys(cat).map((k) => `${ns}:${k}`));

  return SUPPORTED_LOCALES.filter((locale) => {
    if (locale === SOURCE_LOCALE) return true;
    const candidate = resources[locale];
    if (!candidate) return false;
    return requiredKeys.every((full) => {
      const [ns, key] = full.split(':');
      if (ns === undefined || key === undefined) return false;
      const value = candidate[ns]?.[key];
      return typeof value === 'string' && value.trim() !== '';
    });
  });
}

export { i18next };
